import { useTranslation } from 'next-i18next'
import { type FC } from 'react'

import { isValidCategory } from '@/domain/category/isValidCategory'
import { getDescriptionFromSelectedValues } from '@/domain/srp/filters/getDescriptionFromSelectedValues'
import { getCategoryLevel } from '@/domain/srp/getCategoryLevel'
import {
  type AttributeFilter,
  type AttributeValue,
  type GetSearchResultsPageByUrlQuery,
} from '@/generated'
import { useGetSearchCategory } from '@/hooks/category/useGetSearchCategory'
import { type SearchActionsResult } from '@/hooks/srp/useSearchActions'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { AccordionItem } from '@/ui/atoms/accordion'
import { sendToLogger } from '@/utils/sendToLogger'

import { TreeFilter } from './TreeFilter'

export type CategoryTreeFilterProps = {
  data?: GetSearchResultsPageByUrlQuery['searchResultsPageByUrl']
  /**
   * Filtering list
   * */
  categoryTree: AttributeFilter
  refetchResults: SearchActionsResult['refetchResults']
  setLoadingStates: (params: { filters?: boolean; results?: boolean }) => void
}

export const CategoryTreeFilter: FC<CategoryTreeFilterProps> = ({
  data,
  categoryTree,
  refetchResults,
  setLoadingStates,
}) => {
  const { updateSearchCategory } = useGetSearchCategory()
  const { t } = useTranslation('srp')

  const searchQuery = data?.searchQuery
  const categoryId = searchQuery?.category?.id
  const description = getDescriptionFromSelectedValues(
    categoryTree?.selectedValues,
    categoryTree?.values
  )

  const handleCategoryChange = async (selectedCategoryId: number): Promise<void> => {
    const categoryLevel = getCategoryLevel({ categoryId: selectedCategoryId })

    trackEvent({
      action: GA_EVENT.CategorySelectAttempt,
      label: `cat${categoryLevel}=${selectedCategoryId}`,
    })

    try {
      await refetchResults(data, { selectedCategoryId, newPage: 1 })
      trackEvent({
        action: GA_EVENT.CategorySelectSuccess,
        label: `cat${categoryLevel}=${selectedCategoryId}`,
      })
    } catch (e) {
      const error = e as Error
      sendToLogger(error, { fingerprint: ['CategoryTreeFilter'] })
      trackEvent({
        action: GA_EVENT.CategorySelectFail,
        label: `cat${categoryLevel}=${selectedCategoryId};error=${error.message}`,
      })
    }
  }

  const handleSelectCategory = async (categoryLeaf: AttributeValue) => {
    if (!categoryLeaf || categoryLeaf.value === categoryId?.toString()) return
    setLoadingStates({ filters: true, results: true })

    /** It should only fetch the SEO URL if there isn't one */
    if (!categoryLeaf.seoUrl) {
      await handleCategoryChange(parseInt(categoryLeaf.value))
      updateSearchCategory({
        id: parseInt(categoryLeaf.value),
        localizedName: categoryLeaf.label,
        parentId: isValidCategory(parseInt(`${categoryLeaf.parentValue}`))
          ? parseInt(`${categoryLeaf.parentValue}`)
          : null,
      })
    }
  }

  const handleAccordionClick = () => {
    trackEvent({ action: GA_EVENT.CategorySelectBegin })
  }

  if (!categoryTree) return null

  return (
    <AccordionItem
      id="categorySection"
      onClick={handleAccordionClick}
      title={t('filters.category.title')}
      description={description}
    >
      <TreeFilter
        attributes={categoryTree}
        handleSelectAttribute={handleSelectCategory}
        selectedId={categoryId}
        shouldSelectLeaf
        type="category"
      />
    </AccordionItem>
  )
}
